import { extractLocationDetails } from './locationPicker.utils';
import { LocationDetailsDto, LocationSource } from './locationService.dto';

export interface MapPrediction {
  placeId: string;
  description: string;
}

const getAutocompleteService = () => new google.maps.places.AutocompleteService();

const getPlacesService = () => new google.maps.places.PlacesService(document.createElement('div'));

const getGeocoder = () => new google.maps.Geocoder();

export const fetchPlacePredictions = (
  input: string,
  locationBias?: { lat: number; lng: number }
): Promise<MapPrediction[]> =>
  new Promise((resolve, reject) => {
    const request: google.maps.places.AutocompletionRequest = {
      input,
      componentRestrictions: { country: 'us' },
      types: ['geocode'],
    };

    if (locationBias) {
      request.location = new google.maps.LatLng(locationBias.lat, locationBias.lng);
      request.radius = 1000;
    }

    getAutocompleteService().getPlacePredictions(request, (predictions, status) => {
      if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
        resolve(
          predictions.map(prediction => ({
            placeId: prediction.place_id,
            description: prediction.description,
          }))
        );
        return;
      }

      if (
        status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS ||
        status === google.maps.places.PlacesServiceStatus.OK
      ) {
        resolve([]);
        return;
      }

      reject(new Error(`Places autocomplete failed: ${status}`));
    });
  });

export const fetchPlaceDetails = (placeId: string): Promise<LocationDetailsDto> =>
  new Promise((resolve, reject) => {
    getPlacesService().getDetails(
      {
        placeId,
        fields: ['address_component', 'geometry', 'name', 'formatted_address'],
      },
      (result, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && result) {
          resolve(extractLocationDetails(result, 'MAP'));
          return;
        }
        reject(new Error(`Place details failed: ${status}`));
      }
    );
  });

export const reverseGeocode = (
  lat: number,
  lng: number,
  source: LocationSource = 'GPS'
): Promise<LocationDetailsDto | undefined> =>
  new Promise(resolve => {
    getGeocoder().geocode({ location: { lat, lng } }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results?.length) {
        resolve(extractLocationDetails(results[0], source, { lat, lng }));
        return;
      }
      resolve(undefined);
    });
  });
